import axios from 'axios';
import { apiUrl, BACKEND_URL } from '@/config/api';
import { getAuthToken, getUser } from '@/utils/auth';
import { rememberChat, rememberBookmark, rememberCheckin, rememberAnalysis, removeLocalBookmark, patchLocalCheckin, removeLocalCheckin } from './localStore';

export function friendlyMemberError(error) {
  const status = error?.status;
  const detail = error?.payload?.detail;
  const raw = typeof detail === 'string' ? detail : detail?.message || error?.message || '';
  if (status === 401) return 'Please sign in again to keep talking with Vani.';
  if (status === 402) return 'Upgrade to keep talking with Vani.';
  if (status === 429) return 'Give Vani a moment, then send that again.';
  if (status === 503 || /starting up|temporarily unavailable/i.test(raw)) {
    return 'Vani is waking up. Try once more in a few seconds.';
  }
  if (typeof raw === 'string' && raw.trim() && raw.length < 180 && !/^Request failed/i.test(raw)) return raw;
  return 'Vani could not finish that answer. Please try again.';
}

function headers() {
  const next = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) next.Authorization = `Bearer ${token}`;
  return next;
}

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    credentials: 'include',
    headers: { ...headers(), ...(options.headers || {}) },
    ...options,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(friendlyMemberError({ status: response.status, payload: data, message: data.detail?.message || data.detail || data.message }));
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return data;
}

export const memberApi = {
  bootstrap: () => request('/api/member/bootstrap'),
  conversations: () => request('/api/member/conversations'),
  conversation: (id) => request(`/api/member/conversations/${id}`),
  patchConversation: (id, body) =>
    request(`/api/member/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteConversation: (id) =>
    request(`/api/member/conversations/${id}`, { method: 'DELETE' }),
  chat: async (body) => {
    const result = await request('/api/member/chat', { method: 'POST', body: JSON.stringify(body) });
    rememberChat(getUser(), result);
    return result;
  },
  bookmarks: () => request('/api/member/bookmarks'),
  addBookmark: async (body) => {
    const result = await request('/api/member/bookmarks', { method: 'POST', body: JSON.stringify(body) });
    rememberBookmark(getUser(), result);
    return result;
  },
  deleteBookmark: async (id) => {
    const result = await request(`/api/member/bookmarks/${id}`, { method: 'DELETE' });
    removeLocalBookmark(getUser(), id);
    return result;
  },
  checkins: () => request('/api/member/checkins'),
  checkinSummary: () => request('/api/member/checkins/summary'),
  checkinClinicBrief: (lookbackDays = 14) =>
    request(`/api/member/checkins/clinic-brief?lookback_days=${encodeURIComponent(lookbackDays)}`),
  addCheckin: async (body) => {
    const result = await request('/api/member/checkins', { method: 'POST', body: JSON.stringify(body) });
    rememberCheckin(getUser(), result);
    return result;
  },
  patchCheckin: async (id, body) => {
    const result = await request(`/api/member/checkins/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    // Prefer the returned document (includes appended logs) over the request body.
    if (result && result.id) {
      patchLocalCheckin(getUser(), id, result);
    } else {
      patchLocalCheckin(getUser(), id, body);
    }
    return result;
  },
  documents: () => request('/api/documents'),
  document: (id) => request(`/api/documents/${encodeURIComponent(id)}`),
  deleteDocument: (id) => request(`/api/documents/${id}`, { method: 'DELETE' }),
  analyzeDocument: (id) =>
    request(`/api/documents/${encodeURIComponent(id)}/analyze`, { method: 'POST', body: '{}' }),
  retryDocument: (id) =>
    request(`/api/documents/${encodeURIComponent(id)}/retry`, { method: 'POST', body: '{}' }),
  deleteCheckin: async (id) => {
    const result = await request(`/api/member/checkins/${id}`, { method: 'DELETE' });
    removeLocalCheckin(getUser(), id);
    return result;
  },
  pushVapidKey: () => request('/api/member/push/vapid-public-key'),
  pushStatus: () => request('/api/member/push/status'),
  pushSubscribe: (body) => request('/api/member/push/subscribe', { method: 'POST', body: JSON.stringify(body) }),
  pushUnsubscribe: (body) =>
    request('/api/member/push/subscribe', { method: 'DELETE', body: JSON.stringify(body) }),
  pushTest: () => request('/api/member/push/test', { method: 'POST', body: JSON.stringify({}) }),
  feedback: (body) => request('/api/member/feedback', { method: 'POST', body: JSON.stringify(body) }),
  analyses: () => request('/api/member/analyses'),
  createAnalysis: async (body) => {
    const result = await request('/api/member/analyses', { method: 'POST', body: JSON.stringify(body) });
    rememberAnalysis(getUser(), result);
    return result;
  },
  // Phase 2: Peer Community
  communityGroups: () => request('/api/community/groups'),
  communityGroup: (id) => request(`/api/community/groups/${id}`),
  communityFeed: (skip = 0, limit = 20) => request(`/api/community/feed?skip=${skip}&limit=${limit}`),
  communityGroupFeed: (groupId, skip = 0, limit = 20) => request(`/api/community/groups/${groupId}/posts?skip=${skip}&limit=${limit}`),
  createCommunityPost: (body) => request('/api/community/posts', { method: 'POST', body: JSON.stringify(body) }),
  toggleCommunityPostReaction: (postId, body) => request(`/api/community/posts/${postId}/reactions`, { method: 'POST', body: JSON.stringify(body) }),
  communityComments: (postId) => request(`/api/community/posts/${postId}/comments`),
  createCommunityComment: (postId, body) => request(`/api/community/posts/${postId}/comments`, { method: 'POST', body: JSON.stringify(body) }),
  reportCommunityPost: (postId) => request(`/api/community/posts/${postId}/report`, { method: 'POST' }),
  reportCommunityComment: (commentId) => request(`/api/community/comments/${commentId}/report`, { method: 'POST' }),
  communityConversations: () => request('/api/community/messages/users'),
  communityMessageHistory: (otherUserId) => request(`/api/community/messages/${otherUserId}`),
  communityEvents: () => request('/api/community/events'),
  toggleCommunityEventRSVP: (eventId) => request(`/api/community/events/${eventId}/rsvp`, { method: 'POST' }),
  // Phase 3: Clinical Trial Matching
  getTrialProfile: () => request('/api/clinical-trials/profile'),
  updateTrialProfile: (body) => request('/api/clinical-trials/profile', { method: 'PUT', body: JSON.stringify(body) }),
  getTrialMatches: () => request('/api/clinical-trials/matches'),
  saveTrialMatch: (body) => request('/api/clinical-trials/saved', { method: 'POST', body: JSON.stringify(body) }),
  getSavedTrials: () => request('/api/clinical-trials/saved'),
  unsaveTrialMatch: (trialId) => request(`/api/clinical-trials/saved/${trialId}`, { method: 'DELETE' }),
  getTrialBrief: (trialId) => request(`/api/clinical-trials/trials/${trialId}/brief`),
};

export async function fetchHospitals() {
  try {
    const data = await request('/api/hospitals');
    return Array.isArray(data) ? data : data.hospitals || [];
  } catch {
    return [];
  }
}

// Default export of a pre-configured axios client for page requests expecting axios response shape
const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;
